import React from "react";
import { Field, Form } from "react-final-form";
import { Button, Modal, TextAreaInput } from "@pickit/pickit-components";
import "./rejectInvoice.scss";
import { RejectInvoiceForm, RejectInvoiceProps } from "./types";
import { formatDDMMYYYY } from "utils/date";
import Select from "component/inputs/Select";
import {
  INVOICE_REJECT_REASONS,
  PRELIQUIDATION_STATUS_NAMES,
  VALIDATION_REGEX,
} from "utils/constants";
import useValidationSchema from "hooks/useValidationSchema";
import "./i18n";
import "../i18n";
import { useTranslation } from "react-i18next";

const id = "reject-invoice";

export const RejectInvoice: React.FC<RejectInvoiceProps> = ({
  formRef,
  preliquidation,
  validationSchema,
  onSubmit,
  onClose,
  rejectedReasons,
  isOpen,
  testId,
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Modal
      width="1190px"
      height="496px"
      isOpen={isOpen}
      onClose={onClose}
      closeOnBlur={false}
    >
      <div className="reject-invoice-modal-container">
        <div className="reject-invoice-modal-header">
          <div className="reject-invoice-modal-header-number">
            <label data-testId={`${testId}-preliquidation-id-label`}>{t("rejectInvoice:preliquidationNumber")}</label>
            <p
              data-testId={`${testId}-preliquidation-id`}
              id={id + "-label-presettlement"}
            >
              {preliquidation.id}
            </p>
          </div>
          <div className="reject-invoice-modal-header-status">
            <label data-testId={`${testId}-preliquidation-status-label`}>{t("rejectInvoice:status")}</label>
            <p
              data-testId={`${testId}-preliquidation-status`}
              id={id + "-label-status"}
            >
              {t(`${PRELIQUIDATION_STATUS_NAMES[preliquidation.status]}`)}
            </p>
          </div>
          <div className="reject-invoice-modal-header-date">
            <label data-testId={`${testId}-date-issue`}>
              {t("rejectInvoice:dateOfIssue")}
            </label>
            {preliquidation.generatedAt && (
              <p id={id + "-label-generatedAt"} data-testId={`${testId}-date-issue-value`}>
                {formatDDMMYYYY(preliquidation.generatedAt)}
              </p>
            )}
          </div>
        </div>
        <div className="reject-invoice-modal-content">
          <h6
            data-testId={`${testId}-question`}
            className="reject-invoice-modal-title"
          >
            {t("rejectInvoice:question")}
          </h6>
          <Form
            onSubmit={onSubmit}
            validate={useValidationSchema(validationSchema)}
            mutators={{
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
          >
            {({ form, invalid, values, handleSubmit }) => {
              formRef.current = form;
              return (
                <form
                  className="reject-invoice-modal-form"
                  onSubmit={handleSubmit}
                >
                  <Field
                    id={id + "-select-reason"}
                    type="text"
                    name="reason"
                    label={t("rejectInvoice:reasonLabel")}
                    className="Admin-Pickers-input"
                    placeholder={t("rejectInvoice:reasonPlaceholder")}
                    onChange={form.mutators.setValue}
                    options={rejectedReasons?.map((reason) => ({
                      ...reason,
                      label: t(INVOICE_REJECT_REASONS[reason.tag] || ""),
                    }))}
                    testId={`${testId}-field-reason`}
                  >
                    {(props: any) => <Select {...props} />}
                  </Field>
                  {(values as RejectInvoiceForm)?.reason?.tag === "OTH300" && (
                    <div className="reject-invoice-reason-container">
                      <Field
                        type="text"
                        name="message"
                        id={id + "-textarea-message"}
                        maxLength={150}
                        placeholder={t("rejectInvoice:otherPlaceholder")}
                        pattern={VALIDATION_REGEX.regStreet}
                        testId={`${testId}-field-message`}
                        validate={(value: string) => {
                          if (!value) {
                            return t("validations:required");
                          }
                          return undefined;
                        }}
                      >
                        {(props: any) => <TextAreaInput {...props} />}
                      </Field>
                    </div>
                  )}
                  <div className="button-reject-container">
                    <Button
                      disabled={invalid}
                      testId={`${testId}-button-reject`}
                      label={t("rejectInvoice:reject")}
                      size="medium"
                      width="220"
                      hierarchy="primary"
                      onClick={() => handleSubmit()}
                    />
                  </div>
                </form>
              );
            }}
          </Form>
        </div>
      </div>
    </Modal>
  );
};
